import { useState, useEffect, useCallback } from 'react';
import toastService from '../services/toastService';

const useAddProductForm = (product, categoriesData, catalogItem = null) => {
  const [packagingTypeDefaults, setPackagingTypeDefaults] = useState({ standard: 0, master: 0 });
  const [formData, setFormData] = useState({
    name: '',
    mrp: '',
    buyingPrice: '',
    sellingPrice: '',
    discount: '',
    description: '',
    category: '',
    subcategory: '',
    productCode: '',
    sku: '',
    selectedPackagingType: 'standard',
    status: true,
  });

  const [tieredPricingEnabled, setTieredPricingEnabled] = useState(false);
  const [bulkPricingTiers, setBulkPricingTiers] = useState([
    { minQuantity: '', price: '', discountPercentage: '' },
  ]);
  const [multiplier, setMultiplier] = useState(1);
  const [moqLoose, setMoqLoose] = useState(1); // Renamed to avoid confusion with tiered pricing MOQ

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [keyFeatures, setKeyFeatures] = useState([]);
  const [productImages, setProductImages] = useState([]);

  useEffect(() => {
    if (categoriesData?.data) {
      setCategories(categoriesData.data);
    }
  }, [categoriesData?.data]);

  useEffect(() => {
    if (!product || !categoriesData?.data) {
      return;
    }

    let parentCategory = '';
    let subcategory = '';

    const currentCategories = categoriesData.data;

    currentCategories.forEach((cat) => {
      if (!parentCategory && cat.children?.some((sub) => sub._id === product.category?._id)) {
        parentCategory = cat._id;
        subcategory = product.category._id;
      }
    });

    if (!parentCategory) {
      const foundCategory = currentCategories.find((cat) => cat._id === product.category?._id);
      if (foundCategory) parentCategory = foundCategory._id;
    }

    if (parentCategory) {
      const selectedCategory = currentCategories.find((cat) => cat._id === parentCategory);
      setSubcategories(selectedCategory?.children || []);
    }

    if (product.packagingType) {
      setPackagingTypeDefaults({
        standard: product.packagingType.standard || 0,
        master: product.packagingType.master || 0,
      });
    }

    setFormData((prev) => ({
      ...prev,
      name: product.title || '',
      mrp: product.mrp || '',
      description: product.description || '',
      productCode: product.productCode || '',
      sku: product.sku || '',
      category: parentCategory,
      subcategory: subcategory,
      // Initialize with catalogItem data if available
      buyingPrice: catalogItem?.buyingPrice || '',
      sellingPrice: catalogItem?.sellerPrice || '',
      discount: catalogItem?.discountPercentage || '',
      selectedPackagingType: catalogItem?.packagingType || 'standard',
      status: catalogItem ? catalogItem.status === 'active' : true,
    }));

    setKeyFeatures(product.keyFeatures?.length ? product.keyFeatures : []);

    setProductImages(product.images || []);

    // Initialize tiered pricing from catalogItem
    if (catalogItem?.bulkPricing?.length > 0) {
      setTieredPricingEnabled(true);
      setBulkPricingTiers(catalogItem.bulkPricing);
    } else {
      setTieredPricingEnabled(false);
      setBulkPricingTiers([{ minQuantity: '', price: '', discountPercentage: '' }]);
    }
    setMultiplier(catalogItem?.moqMultiplier || 1);
    setMoqLoose(catalogItem?.packagingType === 'loose' ? 1 : catalogItem?.totalMoqPieces || 1);
  }, [product, catalogItem, categoriesData?.data]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let newFormData = { ...prev, [name]: value };

      const mrp = parseFloat(newFormData.mrp);
      if (isNaN(mrp) || mrp <= 0) {
        // If MRP is invalid, just update the field and return
        return newFormData;
      }

      if (name === 'discount') {
        const discount = parseFloat(value);
        if (!isNaN(discount) && discount >= 0 && discount <= 100) {
          newFormData.sellingPrice = (mrp * (1 - discount / 100)).toFixed(2);
        } else {
          newFormData.sellingPrice = '';
        }
      } else if (name === 'sellingPrice') {
        const sellingPrice = parseFloat(value);
        if (!isNaN(sellingPrice)) {
          if (sellingPrice > mrp) {
            toastService.error('Selling price cannot be greater than MRP.');
            newFormData.sellingPrice = mrp.toFixed(2);
            newFormData.discount = '0.00';
          } else if (sellingPrice >= 0) {
            newFormData.discount = (((mrp - sellingPrice) / mrp) * 100).toFixed(2);
          } else {
            newFormData.discount = '';
          }
        } else {
          newFormData.discount = '';
        }
      } else if (name === 'buyingPrice') {
        const buyingPrice = parseFloat(value);
        if (!isNaN(buyingPrice) && buyingPrice > mrp) {
          toastService.error('Buying price cannot be greater than MRP.');
          newFormData.buyingPrice = mrp.toFixed(2);
        }
      } else if (name === 'selectedPackagingType' && value === 'loose') {
        setMultiplier(1); // Reset multiplier to 1 when 'loose' packaging is selected
        setMoqLoose(1); // Ensure moqLoose is 1 for loose packaging
      }
      return newFormData;
    });
  }, []);

  const handleTieredPricingToggle = useCallback(
    (e) => {
      const enabled = e.target.checked;
      setTieredPricingEnabled(enabled);
      if (enabled && bulkPricingTiers.length === 1 && !bulkPricingTiers[0].minQuantity) {
        setBulkPricingTiers([{ minQuantity: '50', price: '', discountPercentage: '' }]);
      }
    },
    [bulkPricingTiers],
  );

  const addTier = useCallback(() => {
    setBulkPricingTiers((prev) => [
      ...prev,
      { minQuantity: '', price: '', discountPercentage: '' },
    ]);
  }, []);

  const removeTier = useCallback(
    (index) => {
      if (bulkPricingTiers.length > 1) {
        setBulkPricingTiers((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [bulkPricingTiers],
  );

  const updateTier = useCallback((index, updatedTier) => {
    setBulkPricingTiers((prev) => {
      const newTiers = [...prev];
      newTiers[index] = updatedTier;
      return newTiers;
    });
  }, []);

  const validateTieredPricing = useCallback(() => {
    if (!tieredPricingEnabled) return true;

    const validTiers = bulkPricingTiers.filter(
      (tier) => tier.minQuantity && tier.price && tier.discountPercentage !== '',
    );

    if (validTiers.length === 0) {
      toastService.error('Please add at least one valid pricing tier or disable tiered pricing.');
      return false;
    }

    const quantities = validTiers.map((tier) => parseInt(tier.minQuantity));
    const uniqueQuantities = [...new Set(quantities)];
    if (quantities.length !== uniqueQuantities.length) {
      toastService.error('Minimum quantities must be unique for each tier.');
      return false;
    }

    const sortedTiers = [...validTiers].sort(
      (a, b) => parseInt(a.minQuantity) - parseInt(b.minQuantity),
    );

    // Validate: Higher quantity tiers must have lower per-unit prices than lower quantity tiers
    for (let i = 0; i < sortedTiers.length - 1; i++) {
      const currentTier = sortedTiers[i];
      const nextTier = sortedTiers[i + 1];

      if (parseFloat(nextTier.price) >= parseFloat(currentTier.price)) {
        toastService.error(
          `Tier price for ${nextTier.minQuantity} pieces (₹${nextTier.price}) must be lower than for ${currentTier.minQuantity} pieces (₹${currentTier.price}).`,
        );
        return false;
      }
    }

    const baseSellingPrice = parseFloat(formData.sellingPrice);

    for (const tier of validTiers) {
      const tierPrice = parseFloat(tier.price);
      const mrp = parseFloat(formData.mrp);

      if (tierPrice > mrp) {
        toastService.error(`Tier price ₹${tierPrice} cannot exceed MRP ₹${mrp}.`);
        return false;
      }

      // Validate: Bulk price cannot be higher than Base Price
      if (!isNaN(baseSellingPrice) && tierPrice > baseSellingPrice) {
        toastService.error(
          `Tier price ₹${tierPrice} cannot be higher than the base selling price ₹${baseSellingPrice}.`,
        );
        return false;
      }
    }
    return true;
  }, [tieredPricingEnabled, bulkPricingTiers, formData.mrp, formData.sellingPrice]);

  const validateForm = useCallback(() => {
    const { buyingPrice, sellingPrice, selectedPackagingType, mrp } = formData;

    if (!sellingPrice || !selectedPackagingType) {
      toastService.error('Please fill in all required fields');
      return false;
    }

    if (parseFloat(sellingPrice) > parseFloat(mrp)) {
      toastService.error('Selling price cannot be greater than MRP.');
      return false;
    }

    if (parseFloat(buyingPrice) > parseFloat(mrp)) {
      toastService.error('Buying price cannot be greater than MRP.');
      return false;
    }

    return validateTieredPricing();
  }, [formData, validateTieredPricing]);

  const getSubmissionData = useCallback(
    (productId, sellerId) => {
      const bulkPricingData = tieredPricingEnabled
        ? bulkPricingTiers
            .filter((tier) => tier.minQuantity && tier.price && tier.discountPercentage !== '')
            .map((tier) => ({
              minQuantity: parseInt(tier.minQuantity),
              price: parseFloat(tier.price),
              discountPercentage: parseFloat(tier.discountPercentage),
            }))
            .sort((a, b) => a.minQuantity - b.minQuantity)
        : [];

      const calculatedMoq =
        formData.selectedPackagingType === 'loose'
          ? moqLoose * multiplier
          : packagingTypeDefaults[formData.selectedPackagingType] * multiplier || 1;

      return {
        productId,
        sellerId,
        sellerPrice: parseFloat(formData.sellingPrice) || 0,
        buyingPrice: parseFloat(formData.buyingPrice),
        discountPercentage: parseFloat(formData.discount) || 0,
        totalMoqPieces: calculatedMoq,
        moqMultiplier: multiplier,
        packagingType: formData.selectedPackagingType,
        status: formData.status ? 'active' : 'inactive',
        category: formData.subcategory || formData.category,
        bulkPricing: bulkPricingData,
      };
    },
    [formData, tieredPricingEnabled, bulkPricingTiers, moqLoose, multiplier, packagingTypeDefaults],
  );

  const currentTotalMoq =
    formData.selectedPackagingType === 'loose'
      ? moqLoose * multiplier
      : packagingTypeDefaults[formData.selectedPackagingType] * multiplier;

  return {
    formData,
    handleInputChange,
    tieredPricingEnabled,
    setTieredPricingEnabled,
    bulkPricingTiers,
    handleTieredPricingToggle,
    addTier,
    removeTier,
    updateTier,
    multiplier,
    setMultiplier,
    moqLoose,
    setMoqLoose,
    validateForm,
    getSubmissionData,
    categories,
    subcategories,
    keyFeatures,
    productImages,
    currentTotalMoq,
    packagingTypeDefaults, // Expose for display purposes
  };
};

export default useAddProductForm;
